import { type ReactNode, useEffect, useState } from 'react';
import type { Bidder, Lot } from '../data/mockLots';
import { deriveLotState, type LotState } from '../hooks/useLotState';
import { revealProps } from '../lib/motion';
import { BidderAvatar, RepeatBadge } from './BidderPill';
import { LotHero } from './LotHero';

type Props = {
  lot: Lot;
  onTap?: () => void;
};

// Home page hero card — wraps the canonical LotHero (the source of truth for
// the visual, see feedback memory `lot-hero-canonical`). The whole card is a
// button that opens the lot detail page; the home feed is polling-only and
// carries no bid CTA of its own.
export function HomeLotCard({ lot, onTap }: Props) {
  const [flash, setFlash] = useState(0);
  const [state, setState] = useState<LotState>(() => deriveLotState(lot));

  // LotHero handles its own timer ticking, but we still need a coarse state
  // for the CTA visibility (BID only on live, otherwise the card is just a
  // teaser that opens the detail page).
  useEffect(() => {
    setState(deriveLotState(lot));
    const id = window.setInterval(() => setState(deriveLotState(lot)), 1000);
    return () => window.clearInterval(id);
  }, [lot]);

  // bidsCount drives the flash animation; useEffect re-runs when it changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: bidsCount is the trigger
  useEffect(() => {
    setFlash((f) => f + 1);
  }, [lot.bidsCount]);

  // All three states render through HeroSideSlot, which mirrors the left price
  // column's row rhythm (label / value / tally) so the two halves of the strip
  // share one grid and don't drift. With zero bids we still fill the slot so the
  // right half doesn't read as broken/empty. A closed hero is a "recent win"
  // showcase (selectHero rule 5): the live LAST BID cluster becomes the winner,
  // since there is no ongoing bidding.
  const rightSlot =
    state === 'closed' ? (
      <WinnerSlot lot={lot} />
    ) : lot.recentBidders.length > 0 ? (
      <LeaderSlot lot={lot} />
    ) : (
      <EmptyBidders />
    );

  // div + role=button rather than a native <button>: LotHero renders flow
  // content (a <section> with headings) which isn't valid phrasing content
  // inside a <button>, so keyboard activation is wired up manually below.
  const open = () => onTap?.();
  return (
    // biome-ignore lint/a11y/useSemanticElements: LotHero flow content is invalid inside <button>
    <div
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open();
        }
      }}
      {...revealProps(0, {
        className: 'relative block w-full text-left hud-corners overflow-hidden cursor-pointer',
      })}
    >
      <LotHero
        lot={lot}
        state={state}
        livePrice={state === 'live' ? lot.currentPrice : undefined}
        flashTrigger={flash}
        timerEndsAt={state === 'live' ? lot.timerEndsAt : undefined}
        rightSlot={rightSlot}
      />
    </div>
  );
}

// Right half of the hero price strip. self-stretch matches the left price
// column's height; the label pins to the top (aligning with CURRENT PRICE) and
// the tally to the bottom (aligning with the MARKET line — same text-[10px]
// line-box, no leading-none, so the baselines meet). The value row takes the
// remaining space and centers the bidder cluster between them. One component
// morphs across the live (LAST BID), closed (WON BY) and empty states.
function HeroSideSlot({
  label,
  labelClass,
  children,
  tally,
}: {
  label: string;
  labelClass: string;
  children: ReactNode;
  tally: ReactNode;
}) {
  return (
    <div className="flex flex-col items-end self-stretch shrink-0">
      <span className={`font-mono text-[9px] tracking-[0.22em] ${labelClass}`}>{label}</span>
      <div className="flex flex-1 items-center gap-2 min-w-0 max-w-[160px]">{children}</div>
      <span className="font-mono text-[10px] text-fg-dim tabular-nums">{tally}</span>
    </div>
  );
}

// bids · bidders tally with the counts emphasised. Shared by the live and closed
// slots; the empty slot renders its own zeroed variant inline.
function BidTally({ lot }: { lot: Lot }) {
  return (
    <>
      <span className="font-semibold text-fg">{lot.bidsCount}</span> bids ·{' '}
      <span className="font-semibold text-fg">{lot.uniqueBidders}</span> bidders
    </>
  );
}

// Live hero: just the current leader (avatar + name + ×N badge). The overlapping
// BidderCascade is parked for now (exported below) — the leader alone reads
// cleaner here.
function LeaderSlot({ lot }: { lot: Lot }) {
  const leader = lot.recentBidders[0];
  if (!leader) return <EmptyBidders />;
  return (
    <HeroSideSlot label="LAST BID" labelClass="text-primary" tally={<BidTally lot={lot} />}>
      <RepeatBadge count={lot.lastBidderSeq ?? leader.bidCount} />
      <span className="font-medium text-[15px] truncate text-fg">{leader.firstName}</span>
      <BidderAvatar bidder={leader} size="md" />
    </HeroSideSlot>
  );
}

// Closed (recent-win) hero: the winner takes the live leader's spot.
function WinnerSlot({ lot }: { lot: Lot }) {
  const winner = lot.winner;
  if (!winner) return <EmptyBidders />;
  return (
    <HeroSideSlot label="WON BY" labelClass="text-success" tally={<BidTally lot={lot} />}>
      <RepeatBadge count={lot.lastBidderSeq ?? winner.bidCount} />
      <span className="font-medium text-[15px] truncate text-fg">{winner.firstName}</span>
      <BidderAvatar bidder={winner} size="md" />
    </HeroSideSlot>
  );
}

const CASCADE_SHOW = 5;
const CASCADE_AVATAR_SIZE = 28;
const CASCADE_OVERLAP = 10;

// Parked: overlapping last-bidders stack. The home hero now shows just the
// leader (LeaderSlot), but this is kept exported for reuse elsewhere.
export function BidderCascade({ bidders }: { bidders: Bidder[] }) {
  const shown = bidders.slice(0, CASCADE_SHOW);
  if (shown.length === 0) return null;

  const step = CASCADE_AVATAR_SIZE - CASCADE_OVERLAP;
  const width = CASCADE_AVATAR_SIZE + step * (shown.length - 1);

  return (
    <div
      className="relative shrink-0"
      style={{ width, height: CASCADE_AVATAR_SIZE }}
      role="img"
      aria-label={`${shown.length} recent bidders`}
    >
      {shown.map((b, i) => (
        <div
          key={b.bidIndex ?? `${b.id}-${i}`}
          className="absolute top-0 cascade-slot"
          style={{
            left: i * step,
            width: CASCADE_AVATAR_SIZE,
            height: CASCADE_AVATAR_SIZE,
            zIndex: shown.length - i,
          }}
        >
          <div
            className="rounded-full anim-cascade-enter"
            style={{ boxShadow: '0 0 0 2px rgba(10,14,20,0.95)' }}
          >
            <BidderAvatar bidder={b} size="md" />
          </div>
        </div>
      ))}
    </div>
  );
}

// Empty-state for a lot with no bids yet. Reuses the detail page's dashed
// placeholder-avatar look (LotDetail's LastBidSlot) and label styling so the
// empty avatar reads identically across the app — only the surrounding layout
// differs (home stacks the placeholder above its BIDS·BIDDERS tally, the detail
// page lays it out inline beside the hint name).
function EmptyBidders() {
  return (
    <HeroSideSlot
      label="LAST BID"
      labelClass="text-primary"
      tally={
        <>
          <span className="text-fg-dim">0</span> bids · <span className="text-fg-dim">0</span>{' '}
          bidders
        </>
      }
    >
      <span className="font-mono text-[9px] tracking-[0.22em] text-fg-dim">NO BIDDERS</span>
      <div className="w-7 h-7 rounded-full border border-dashed border-fg-dim/50 bg-bg/40 font-mono text-fg-dim flex items-center justify-center shrink-0 text-[13px]">
        —
      </div>
    </HeroSideSlot>
  );
}
